import ReactJsonView from '@microlink/react-json-view'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { ChevronDown, OctagonX } from 'lucide-react'
import React, { type FC, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'

import BrokersMemberContext, { type BrokersMemberContextType } from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { CopyButtonWithAnimationActionIcon } from 'components/reusable/CopyButtonWithAnimation'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'
import { brokersMembers } from 'rails_routes'
import { Badge, Button, CollapsibleContainer } from 'slab'
import { type AuditHistoryParams } from 'types/audit'
import {
  capitalize,
  getLocaleDateString,
} from 'utils'
import {
  getGetBrokersTasksByIdTaskIdResultsQueryKey,
  getGetBrokersTasksTaskIdQueryKey,
  getGetBrokersTaskWorkflowsIdQueryKey,
  useGetBrokersTaskWorkflowsId,
  useGetBrokersUnprocessedEnrollmentsId,
  usePatchBrokersTaskWorkflowsIdMetadata,
} from 'utils/web_api'
import { type BrokersTask, type BrokersWorkflow } from 'utils/web_api.schemas'

import BrokersEnrollment from '../BrokersEnrollment'
import WorkflowMetadataDetails from '../WorkflowMetadataDetails'
import WorkflowProgressBar from '../WorkflowProgressBar'
import AssignBrokersToWorkflow from './AssignBrokersToWorkflow'
import AuditHistoryDrawer from './AuditHistoryDrawer'
import { HoldBadge } from './badges/TypeBadges'
import TaskActionControls, {
  useTaskActions,
} from './TaskActionControls'
import TaskDetailsDialog from './TaskDetailsDialog'
import WorkflowActionControls from './WorkflowActionControls'
import WorkflowDAG from './WorkflowDAG'
import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  getTaskContainerStyle,
  getTaskHeaderBackgroundStyle,
  getTaskHoverStyle,
  getWorkflowBorderColor,
  getWorkflowHeaderBackgroundStyle,
  TaskStatusBadge,
  WorkflowStatusBadge,
} from './WorkflowVisualIndicators'

// Animated Chevron Component for audit history toggles
const AnimatedChevron: FC<{ isOpen: boolean }> = ({ isOpen }) => {
  return (
    <ChevronDown
      size={14}
      className={clsx(
        'transition-transform duration-200 ease-in-out',
        {
          'rotate-0': isOpen,
          '-rotate-90': !isOpen,
        }
      )}
    />
  )
}

type WorkflowDetailsProps = {
  stripePublishableKey: string
  isUserThatchAdmin: boolean
  isUserThatchWorkflowAdmin: boolean
  workflowId?: string // Optional prop to override useParams
}

const WorkflowDetails: FC<WorkflowDetailsProps> = ({
  stripePublishableKey,
  isUserThatchAdmin,
  isUserThatchWorkflowAdmin,
  workflowId,
}) => {
  const { id } = useParams<{ id: string }>()

  // Use workflowId prop if provided, otherwise fall back to params
  const effectiveWorkflowId = workflowId || id

  const {
    data: workflow,
    isLoading,
    error,
  } = useGetBrokersTaskWorkflowsId(
    id || '',
    { evaluate_readiness: true }, // API params - evaluate tasks in detail view
    {
      query: {
        // NOTE: Refetch interval is needed to ensure the workflow is updated in real-time. Automated jobs
        // are run asynchronously, so the workflow may not be updated immediately.
        refetchInterval: (query) => {
          // Stop refetching if the workflow is completed or canceled.
          const isWorkflowFinalized = query.state.data?.status === 'completed' || query.state.data?.status === 'canceled'
          return isWorkflowFinalized ? false : 3000
        },
      },
    }
  )
  const queryClient = useQueryClient()
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const { handleTaskAction, isLoading: taskActionLoading } = useTaskActions(
    effectiveWorkflowId || '',
    {
      enrollmentId: workflow?.insurance_enrollment_id,
      memberId: workflow?.enrollment_info?.member_id,
      onSuccess: (taskId) => {
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTasksTaskIdQueryKey(taskId),
        })
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTasksByIdTaskIdResultsQueryKey(taskId),
        })
      },
    }
  )

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 pb-6'>
        <div className='p-8 text-center text-red-500'>
          Error: {(error as GrapeAPIErrorType).message}
        </div>
      </div>
    )
  }

  if (!workflow) {
    return (
      <div className='container mx-auto px-4 pb-6'>
        <div className='p-8 text-center text-gray-500'>Workflow not found</div>
      </div>
    )
  }

  const handleViewTaskDetails = (taskId: string) => {
    setSelectedTaskId(taskId)
    setIsTaskModalOpen(true)
  }

  const handleTaskDetailsOpenChange = (open: boolean) => {
    setIsTaskModalOpen(open)
    if (!open) {
      setSelectedTaskId(null)
    }
  }

  return (
    <BrokersMemberContext.Provider
      value={{
        stripePublishableKey,
        isUserThatchAdmin,
        isUserThatchWorkflowAdmin,
        memberId: workflow.enrollment_info?.member_id || '',
        planYear: workflow.enrollment_info?.plan_year || 0,
      }}
    >
      <div className='container mx-auto px-4 pb-6'>
        <div className='space-y-6'>
          {/* Workflow Overview */}
          <WorkflowOverview workflow={workflow} />

          {/* Enrollment Details */}
          <EnrollmentDetails
            enrollmentId={workflow.insurance_enrollment_id}
            memberId={workflow.enrollment_info?.member_id || ''}
          />

          {/* Workflow DAG */}
          <WorkflowDAGSection tasks={workflow.tasks_summary || []} />

          {/* Tasks List */}
          <TasksList
            tasks={workflow.tasks_summary || []}
            handleTaskAction={handleTaskAction}
            taskActionLoading={taskActionLoading}
            workflowId={workflow.id}
            enrollmentId={workflow.insurance_enrollment_id}
            onViewTaskDetails={handleViewTaskDetails}
          />
        </div>
      </div>
      <TaskDetailsDialog
        open={isTaskModalOpen && Boolean(selectedTaskId)}
        onOpenChange={handleTaskDetailsOpenChange}
        taskId={selectedTaskId}
        workflowId={workflow.id}
        enrollmentId={workflow.insurance_enrollment_id}
        handleTaskAction={handleTaskAction}
        taskActionLoading={taskActionLoading}
      />
    </BrokersMemberContext.Provider>
  )
}

// Progress Summary Component
const ProgressSummary = ({
  completed,
  total,
  failed,
  excluded,
}: {
  completed: number
  total: number
  failed: number
  excluded?: number
}) => {
  return (
    <div className='space-y-3'>
      <div className='flex justify-between text-sm'>
        <span>Task Progress</span>
        <span>
          {completed}/{total} completed
          {failed > 0 && `, ${failed} failed`}
          {excluded && excluded > 0 && `, ${excluded} excluded`}
        </span>
      </div>
    </div>
  )
}

// Format workflow type similar to WorkflowSummary
const formatWorkflowType = (type: string) => {
  return type.split('_').map(capitalize).join(' ')
}

// Workflow Overview Component
const WorkflowOverview = ({ workflow }: { workflow: BrokersWorkflow }) => {
  const { isUserThatchWorkflowAdmin } = useContext(BrokersMemberContext)
  const [auditHistoryOpen, setAuditHistoryOpen] = useState(false)
  const [isMetadataEditorOpen, setIsMetadataEditorOpen] = useState(false)

  const hasMetadata = Boolean(workflow.metadata && Object.keys(workflow.metadata).length > 0)
  const shouldShowRawMetadata = hasMetadata || Boolean(isUserThatchWorkflowAdmin)
  const metadataObject = (workflow.metadata || {}) as Record<string, unknown>

  const handleCopyWorkflowId = async () => {
    try {
      await navigator.clipboard.writeText(workflow.id)
      toast.success('Workflow ID copied to clipboard', {
        position: 'top-center',
      })
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy to clipboard', {
        position: 'top-center',
      })
    }
  }

  const auditHistoryParams: AuditHistoryParams = {
    auditable_type: 'Tasks::Workflow',
    auditable_id: workflow.id,
  }

  return (
    <div
      className={`rounded-lg border shadow-sm transition-all duration-300 ${getWorkflowBorderColor(
        workflow.status
      )}`}
    >
      {/* Header with dynamic background color */}
      <div
        className='px-6 py-4 border-b transition-colors duration-300'
        style={getWorkflowHeaderBackgroundStyle(workflow.status)}
      >
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-4'>
            <h1 className='text-2xl font-bold text-gray-900'>
              {formatWorkflowType(workflow.workflow_type)}
            </h1>
            <Badge
              type='neutral'
              bordered
              actionIcon={CopyButtonWithAnimationActionIcon}
              actionLabel='Copy Workflow ID'
              onActionClick={handleCopyWorkflowId}
            >
              {workflow.id.slice(-6)}
            </Badge>
            <WorkflowStatusBadge status={workflow.status} />
            <AssignBrokersToWorkflow
              id={workflow.id}
              assignedBrokers={workflow.brokers || []}
              enrollmentId={workflow.insurance_enrollment_id}
              memberId={workflow.enrollment_info?.member_id}
            />
          </div>
          <div className='flex items-center space-x-3'>
            <div className='text-sm text-gray-500'>
              {workflow.progress_percentage?.toFixed(0)}% Complete
            </div>
            <WorkflowActionControls workflow={workflow} />
          </div>
        </div>
      </div>

      {/* Content with white background */}
      <div className='bg-white p-6'>
        <div className='space-y-6'>
          {/* Progress */}
          <div className='space-y-3'>
            <ProgressSummary
              completed={workflow.completed_task_count}
              total={workflow.task_count}
              failed={workflow.failed_task_count}
              excluded={workflow.tasks_summary?.filter(task => task.status === 'excluded').length}
            />
            <WorkflowProgressBar tasks={workflow.tasks_summary || []} />
          </div>

          {/* Details Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4 border-t'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Member
              </label>
              <p className='text-gray-900'>
                {workflow.enrollment_info?.member_name || 'N/A'}
              </p>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Carrier
              </label>
              <p className='text-gray-900'>
                {workflow.enrollment_info?.carrier_name || 'N/A'}
              </p>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Enrollment ID
              </label>
              <p className='text-gray-900 font-mono text-sm'>
                {workflow.insurance_enrollment_id}
              </p>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Created
              </label>
              <p className='text-gray-900'>
                {getLocaleDateString(new Date(workflow.created_at))}
              </p>
            </div>
            {workflow.started_at && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Started
                </label>
                <p className='text-gray-900'>
                  {getLocaleDateString(new Date(workflow.started_at))}
                </p>
              </div>
            )}
            {workflow.completed_at && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Completed
                </label>
                <p className='text-gray-900'>
                  {getLocaleDateString(new Date(workflow.completed_at))}
                </p>
              </div>
            )}
          </div>

          {/* Workflow Metadata Details */}
          <WorkflowMetadataDetails workflow={workflow} headerVariant='full' />

          {/* Raw Metadata */}
          {shouldShowRawMetadata && (
            <div className='pt-4 border-t space-y-2'>
              <CollapsibleContainer
                heading='Raw Workflow Metadata'
                defaultOpen={false}
              >
                <div className='mt-4 p-4'>
                  {hasMetadata
                    ? (
                      <ReactJsonView
                        src={metadataObject}
                        theme={'github' as any}
                        collapsed={1}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={false}
                      />
                      )
                    : (
                      <p className='text-sm text-gray-500'>
                        No workflow metadata is currently stored for this workflow.
                      </p>
                      )}
                </div>
              </CollapsibleContainer>
              {isUserThatchWorkflowAdmin && (
                <WorkflowMetadataEditorDialog
                  open={isMetadataEditorOpen}
                  onOpenChange={setIsMetadataEditorOpen}
                  workflowId={workflow.id}
                  currentMetadata={workflow.metadata as Record<string, unknown> | null}
                />
              )}
            </div>
          )}
          <div className='flex items-center'>
            <Button
              type='button'
              variant='tertiary-deemphasized'
              size='small'
              onClick={() => setAuditHistoryOpen(!auditHistoryOpen)}
            >
              <div className='flex items-center gap-2'>
                Workflow Audit History
                <AnimatedChevron isOpen={auditHistoryOpen} />
              </div>
            </Button>
            <div className='ml-auto flex items-center gap-2'>
              <Button
                type='button'
                size='small'
                variant='primary'
                onClick={() => {
                  window.open(brokersMembers.show({ id: workflow.enrollment_info?.member_id }), '_blank')
                }}
              >
                Broker Portal
              </Button>
              {isUserThatchWorkflowAdmin && (
                <Button
                  type='button'
                  size='small'
                  variant='secondary'
                  onClick={() => setIsMetadataEditorOpen(true)}
                >
                  Modify metadata
                </Button>
              )}
            </div>
          </div>

        </div>
        {/* Audit History Drawer */}
        <AuditHistoryDrawer
          params={auditHistoryParams}
          isOpen={auditHistoryOpen}
          title='Workflow Audit History'
        />
      </div>
    </div>
  )
}

type WorkflowMetadataEditorDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  workflowId: string
  currentMetadata?: Record<string, unknown> | null
}

const formatMetadataForEditor = (metadata?: Record<string, unknown> | null) => {
  return JSON.stringify(metadata ?? {}, null, 2)
}

const WorkflowMetadataEditorDialog: FC<WorkflowMetadataEditorDialogProps> = ({
  open,
  onOpenChange,
  workflowId,
  currentMetadata,
}) => {
  const queryClient = useQueryClient()
  const [metadataDraft, setMetadataDraft] = useState(formatMetadataForEditor(currentMetadata))
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setMetadataDraft(formatMetadataForEditor(currentMetadata))
      setValidationError(null)
    }
  }, [currentMetadata, open])

  const mutation = usePatchBrokersTaskWorkflowsIdMetadata()

  const handleSave = async () => {
    setValidationError(null)

    let parsedDraft: unknown
    try {
      parsedDraft = JSON.parse(metadataDraft)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Metadata must be valid JSON.'
      setValidationError('Invalid JSON: ' + errorMessage)
      return
    }

    if (!parsedDraft || Array.isArray(parsedDraft) || typeof parsedDraft !== 'object') {
      setValidationError('Metadata must be a JSON object.')
      return
    }

    try {
      const updatedWorkflow = await mutation.mutateAsync({
        id: workflowId,
        data: {
          metadata: parsedDraft as Record<string, unknown>,
        },
      })
      queryClient.setQueryData(
        getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
        updatedWorkflow
      )
      queryClient.invalidateQueries({
        queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
      })
      toast.success('Workflow metadata updated successfully', { position: 'top-center' })
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update workflow metadata'
      toast.error(message, { position: 'top-center' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Modify workflow metadata</DialogTitle>
          <DialogDescription>
            Replace the entire workflow metadata payload with valid JSON.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3'>
          <Label htmlFor='workflow-metadata-editor'>Metadata JSON</Label>
          <Textarea
            id='workflow-metadata-editor'
            value={metadataDraft}
            onChange={(event) => {
              setMetadataDraft(event.target.value)
              if (validationError) setValidationError(null)
            }}
            rows={18}
            className='font-mono text-sm'
          />
          {validationError && (
            <p className='text-sm text-red-600'>{validationError}</p>
          )}
          <p className='text-xs text-gray-500'>
            Changes overwrite the existing metadata object and take effect immediately for all tasks.
          </p>
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='tertiary-deemphasized'
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={handleSave}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Task Card Component
const TaskCard = ({
  task,
  handleTaskAction,
  taskActionLoading,
  workflowId,
  enrollmentId,
  onViewTaskDetails,
}: {
  task: BrokersTask
  handleTaskAction: (taskId: string, action: string) => void
  taskActionLoading: boolean
  workflowId: string
  enrollmentId: string
  onViewTaskDetails: (taskId: string) => void
}) => {
  const [taskAuditHistoryOpen, setTaskAuditHistoryOpen] = useState(false)
  const formatTaskType = (type: string) => {
    return type
      .replace(/Task$/, '')
      .replace(/([A-Z])/g, ' $1')
      .trim()
  }

  const { isUserThatchWorkflowAdmin } = useContext<BrokersMemberContextType>(BrokersMemberContext)

  const handleCopyTaskId = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent collapsible from toggling
    try {
      navigator.clipboard.writeText(task.id)
      toast.success('Task ID copied to clipboard', {
        position: 'top-center',
      })
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy to clipboard', {
        position: 'top-center',
      })
    }
  }

  // Task header content for the collapsible trigger (left side)
  const taskHeaderContent = (
    <div className='flex items-center gap-3'>
      <div>
        <h3 className='font-semibold text-lg text-gray-900'>
          {task.display_name || formatTaskType(task.task_type || '')}
        </h3>
      </div>
      <Badge
        type='neutral'
        bordered
        actionIcon={CopyButtonWithAnimationActionIcon}
        actionLabel='Copy Task ID'
        onActionClick={handleCopyTaskId as any}
      >
        {task.id.slice(-6)}
      </Badge>
    </div>
  )

  // Task header icon content (right side) - uses CollapsibleContainer's headingIconContent prop
  const taskHeaderIconContent = (
    <div className='flex items-center gap-2'>
      <HoldBadge isOnHold={task.has_active_hold || false} />
      <TaskStatusBadge status={task.status} isExecutable={task.can_execute} />
      <ExecutionMethodBadge method={task.execution_method} />
      <ExecutionContextBadge context={task.execution_context} />
      {task.sequence_order && (
        <span className='text-xs text-gray-400'>#{task.sequence_order}</span>
      )}
    </div>
  )

  // Task content (details, message, actions)
  const taskContent = (
    <>
      <div className='p-4 space-y-4'>
        {/* Task Details */}
        <div className='grid grid-cols-2 gap-4 text-sm'>
          <div>
            <label className='block font-medium text-gray-700 mb-1'>
              Created
            </label>
            <p className='text-gray-900'>
              {getLocaleDateString(new Date(task.created_at))}
            </p>
          </div>
          <div>
            <label className='block font-medium text-gray-700 mb-1'>
              Updated
            </label>
            <p className='text-gray-900'>
              {getLocaleDateString(new Date(task.updated_at))}
            </p>
          </div>
        </div>

        {/* Task Message */}
        {task.message && (
          <div>
            <label className='block font-medium text-gray-700 mb-1 text-sm'>
              Message
            </label>
            <p className='text-gray-900 text-sm bg-gray-50 p-2 rounded border'>
              {task.message}
            </p>
          </div>
        )}

        {/* Task Blocking Info */}
        {!task.can_execute && task.status === 'pending' && (
          <div className='text-sm text-amber-700 bg-amber-100 rounded p-2 flex items-center gap-2'>
            <span className='font-medium flex items-center gap-2'>
              <OctagonX size={16} />
              Blocked:
            </span>
            <span className='text-gray-600'>
              Waiting for dependencies to complete
            </span>
          </div>
        )}

        {/* Task Actions */}
        <div className='flex items-center justify-between pt-2 border-t border-gray-200'>
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              variant='tertiary-deemphasized'
              size='small'
              onClick={() => setTaskAuditHistoryOpen(!taskAuditHistoryOpen)}
            >
              <div className='flex items-center gap-2'>
                Task Audit History
                <AnimatedChevron isOpen={taskAuditHistoryOpen} />
              </div>
            </Button>
            {isUserThatchWorkflowAdmin && (
              <Button
                type='button'
                variant='secondary'
                size='small'
                onClick={() => onViewTaskDetails(task.id)}
              >
                View Details
              </Button>
            )}
          </div>
          <TaskActionControls
            task={task}
            onTaskAction={handleTaskAction}
            isLoading={taskActionLoading}
            workflowId={workflowId}
            enrollmentId={enrollmentId}
          />
        </div>

      </div>
      {/* Task Audit History Drawer */}
      <AuditHistoryDrawer
        params={{
          auditable_type: 'Tasks::Task',
          auditable_id: task.id,
        }}
        isOpen={taskAuditHistoryOpen}
        title={`Task Audit History - ${task.display_name || formatTaskType(task.task_type || '')}`}
        containerClasses='px-4'
      />
    </>
  )

  // Determine if task should be open by default (not completed)
  const isOpenByDefault = task.status !== 'completed'

  // Apply status-based styling only to a wrapper div
  const taskContainerStyle = getTaskContainerStyle(task)
  const taskHoverStyle = getTaskHoverStyle(task)

  return (
    <div
      className={`${taskContainerStyle} ${taskHoverStyle} rounded-lg task-container`}
      style={
        {
          '--task-header-bg':
            getTaskHeaderBackgroundStyle(task).backgroundColor,
        } as React.CSSProperties
      }
    >
      <CollapsibleContainer
        heading={taskHeaderContent}
        headingIconContent={taskHeaderIconContent}
        defaultOpen={isOpenByDefault}
        headingLevel={4}
      >
        {taskContent}
      </CollapsibleContainer>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          .task-container .trigger {
            background-color: var(--task-header-bg) !important;
          }
        `,
        }}
      />
    </div>
  )
}

// Tasks List Component
const TasksList = ({
  tasks,
  handleTaskAction,
  taskActionLoading,
  workflowId,
  enrollmentId,
  onViewTaskDetails,
}: {
  tasks: BrokersTask[]
  handleTaskAction: (taskId: string, action: string) => void
  taskActionLoading: boolean
  workflowId: string
  enrollmentId: string
  onViewTaskDetails: (taskId: string) => void
}) => {
  if (!tasks || tasks.length === 0) {
    return (
      <div className='bg-white rounded-lg border shadow-sm'>
        <div className='p-6 text-center text-gray-500'>
          No tasks found for this workflow
        </div>
      </div>
    )
  }

  // Sort tasks by sequence order
  const sortedTasks = [...tasks].sort((a, b) => {
    const aOrder = a.sequence_order || 0
    const bOrder = b.sequence_order || 0
    return aOrder - bOrder
  })

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold text-gray-900'>
          Tasks ({tasks.length})
        </h3>
      </div>

      <div className='space-y-4'>
        {sortedTasks.map((task, _index) => (
          <TaskCard
            key={task.id}
            task={task}
            handleTaskAction={handleTaskAction}
            taskActionLoading={taskActionLoading}
            workflowId={workflowId}
            enrollmentId={enrollmentId}
            onViewTaskDetails={onViewTaskDetails}
          />
        ))}
      </div>
    </div>
  )
}

// Enrollment Details Component
const EnrollmentDetails: FC<{ enrollmentId: string; memberId: string }> = ({
  enrollmentId,
  memberId,
}) => {
  const {
    data: enrollment,
    isLoading,
    isError,
  } = useGetBrokersUnprocessedEnrollmentsId(enrollmentId)

  if (isLoading) {
    return (
      <div className='mt-6'>
        <CollapsibleContainer
          heading='Enrollment Details'
          defaultOpen={false}
          headingLevel={3}
        >
          <div className='p-4'>
            <div className='animate-pulse space-y-4'>
              <div className='h-4 bg-gray-200 rounded w-3/4' />
              <div className='h-4 bg-gray-200 rounded w-1/2' />
              <div className='h-4 bg-gray-200 rounded w-2/3' />
            </div>
          </div>
        </CollapsibleContainer>
      </div>
    )
  }

  if (isError || !enrollment) {
    return (
      <div className='mt-6'>
        <CollapsibleContainer
          heading='Enrollment Details'
          defaultOpen={false}
          headingLevel={3}
        >
          <div className='p-4'>
            <p className='text-gray-500'>Unable to load enrollment details</p>
          </div>
        </CollapsibleContainer>
      </div>
    )
  }

  return (
    <div className='mt-6'>
      <CollapsibleContainer
        heading='Enrollment Details'
        defaultOpen={false}
        headingLevel={3}
      >
        <div className='p-4'>
          <BrokersEnrollment
            memberId={memberId}
            enrollmentId={enrollmentId}
            // NOTE: This is a temporary path for the edit coverages button. There are older codepaths that other brokerages use that
            // we are in the process of deprecating.
            // TODO: Remove this once we have deprecated the older codepaths.
            editCoveragesPath={`/brokers/members/${memberId}/enrollments/${enrollment.id}/edit_coverages`}
            embedded
          />
        </div>
      </CollapsibleContainer>
    </div>
  )
}

// Workflow DAG Section Component
const WorkflowDAGSection = ({ tasks }: { tasks: BrokersTask[] }) => {
  const hasV2Tasks = tasks.some(task => task.task_type?.startsWith('Tasks::V2::'))

  if (!hasV2Tasks) {
    return null // Only show DAG for V2 workflows that support dependencies
  }

  return (
    <div className='bg-white rounded-lg border shadow-sm'>
      <CollapsibleContainer
        heading='Workflow Dependency Graph'
        defaultOpen={false}
        headingLevel={3}
      >
        <WorkflowDAG tasks={tasks} />
      </CollapsibleContainer>
    </div>
  )
}

export default WorkflowDetails
